import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Plus, Pencil, Trash2, GripVertical, Save, X, HelpCircle, Languages } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const QuestionManagement = () => {
  const { t, language } = useLanguage();
  const [questions, setQuestions] = useState([]);
  const [symptoms, setSymptoms] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [filterSymptom, setFilterSymptom] = useState('all');
  const [editLanguage, setEditLanguage] = useState('en');
  
  const [formData, setFormData] = useState({
    text: '',
    text_pt: '',
    symptom_ids: [],
    condition_ids: [],
    weight: 1,
    order: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [questionsRes, symptomsRes, conditionsRes] = await Promise.all([
        axios.get(`${API}/questions`),
        axios.get(`${API}/symptoms`),
        axios.get(`${API}/conditions`)
      ]);
      setQuestions(questionsRes.data);
      setSymptoms(symptomsRes.data);
      setConditions(conditionsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      text: '',
      text_pt: '',
      symptom_ids: [],
      condition_ids: [],
      weight: 1,
      order: questions.length
    });
    setEditingQuestion(null);
    setEditLanguage('en');
  };

  const handleOpenDialog = (question = null) => {
    if (question) {
      setEditingQuestion(question);
      setFormData({
        text: question.text || '',
        text_pt: question.text_pt || '',
        symptom_ids: question.symptom_ids || [],
        condition_ids: question.condition_ids || [],
        weight: question.weight || 1,
        order: question.order || 0
      });
    } else {
      resetForm();
    }
    setIsOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate at least one language has text
    if (!formData.text && !formData.text_pt) {
      toast.error('Please enter question text in at least one language');
      return;
    }
    
    try {
      if (editingQuestion) {
        // Partial update - only send fields that have values or changed
        const updatePayload = {};
        
        // Only send text fields if they have values (allows independent editing)
        if (formData.text !== undefined) updatePayload.text = formData.text;
        if (formData.text_pt !== undefined) updatePayload.text_pt = formData.text_pt;
        updatePayload.symptom_ids = formData.symptom_ids;
        updatePayload.condition_ids = formData.condition_ids;
        updatePayload.weight = formData.weight;
        updatePayload.order = formData.order;
        
        await axios.put(`${API}/questions/${editingQuestion.id}`, updatePayload);
        toast.success(t('update') + '!');
      } else {
        // Create new - require at least English text
        if (!formData.text) {
          toast.error('English text is required for new questions');
          return;
        }
        await axios.post(`${API}/questions`, formData);
        toast.success(t('create') + '!');
      }
      fetchData();
      setIsOpen(false);
      resetForm();
    } catch (error) {
      console.error('Failed to save question:', error);
      toast.error('Failed to save question: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;
    try {
      await axios.delete(`${API}/questions/${id}`);
      toast.success(t('delete') + '!');
      fetchData();
    } catch (error) {
      console.error('Failed to delete question:', error);
      toast.error('Failed to delete question');
    }
  };

  const toggleSymptom = (symptomId) => {
    setFormData(prev => ({
      ...prev,
      symptom_ids: prev.symptom_ids.includes(symptomId)
        ? prev.symptom_ids.filter(id => id !== symptomId)
        : [...prev.symptom_ids, symptomId]
    }));
  };

  const toggleCondition = (conditionId) => {
    setFormData(prev => ({
      ...prev,
      condition_ids: prev.condition_ids.includes(conditionId)
        ? prev.condition_ids.filter(id => id !== conditionId)
        : [...prev.condition_ids, conditionId]
    }));
  };

  const filteredQuestions = filterSymptom === 'all'
    ? questions
    : questions.filter(q => q.symptom_ids.includes(filterSymptom));

  // Get display text based on current UI language
  const getQuestionText = (question) => {
    if (language === 'pt' && question.text_pt) return question.text_pt;
    return question.text || question.text_pt || '';
  };

  const getSymptomName = (symptom) => {
    if (language === 'pt' && symptom.name_pt) return symptom.name_pt;
    return symptom.name;
  };

  const getConditionName = (condition) => {
    if (language === 'pt' && condition.name_pt) return condition.name_pt;
    return condition.name;
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-8" data-testid="question-management">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-3">
              <HelpCircle className="w-6 h-6 text-primary" />
              <h1 className="text-3xl font-bold">{t('questionsTitle')}</h1>
            </div>
            <p className="text-muted-foreground mt-1">{t('manageDiagnosticQuestionsDesc')}</p>
          </div>
          
          <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full" onClick={() => handleOpenDialog()} data-testid="add-question-btn">
                <Plus className="w-4 h-4 mr-2" />
                {t('addQuestion')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {editingQuestion ? t('editQuestion') : t('newQuestion')}
                  <Languages className="w-4 h-4 text-muted-foreground" />
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-5 mt-4">
                {/* Language tabs for question text */}
                <Tabs value={editLanguage} onValueChange={setEditLanguage} className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="en" className="flex items-center gap-2">
                      🇬🇧 English
                    </TabsTrigger>
                    <TabsTrigger value="pt" className="flex items-center gap-2">
                      🇵🇹 Português
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="en" className="mt-4">
                    <div className="space-y-2">
                      <Label>{t('questionText')} (English)</Label>
                      <Textarea
                        placeholder="Enter the question in English..."
                        value={formData.text}
                        onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                        data-testid="question-text-en-input"
                        className="min-h-[100px]"
                      />
                    </div>
                  </TabsContent>
                  <TabsContent value="pt" className="mt-4">
                    <div className="space-y-2">
                      <Label>{t('questionText')} (Português)</Label>
                      <Textarea
                        placeholder="Introduza a pergunta em Português..."
                        value={formData.text_pt}
                        onChange={(e) => setFormData({ ...formData, text_pt: e.target.value })}
                        data-testid="question-text-pt-input"
                        className="min-h-[100px]"
                      />
                    </div>
                  </TabsContent>
                </Tabs>

                {/* Show preview of both languages */}
                {editingQuestion && (
                  <div className="p-3 bg-muted rounded-xl text-sm space-y-1">
                    <p><strong>EN:</strong> {formData.text || <span className="text-muted-foreground italic">Not set</span>}</p>
                    <p><strong>PT:</strong> {formData.text_pt || <span className="text-muted-foreground italic">Not set</span>}</p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>{t('linkedSymptoms')}</Label>
                  <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 border rounded-xl">
                    {symptoms.map(symptom => (
                      <div key={symptom.id} className="flex items-center gap-2">
                        <Checkbox
                          id={`sym-${symptom.id}`}
                          checked={formData.symptom_ids.includes(symptom.id)}
                          onCheckedChange={() => toggleSymptom(symptom.id)}
                        />
                        <label htmlFor={`sym-${symptom.id}`} className="text-sm">{getSymptomName(symptom)}</label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('linkedConditions')}</Label>
                  <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 border rounded-xl">
                    {conditions.map(condition => (
                      <div key={condition.id} className="flex items-center gap-2">
                        <Checkbox
                          id={`cond-${condition.id}`}
                          checked={formData.condition_ids.includes(condition.id)}
                          onCheckedChange={() => toggleCondition(condition.id)}
                        />
                        <label htmlFor={`cond-${condition.id}`} className="text-sm">{getConditionName(condition)}</label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('weight')}</Label>
                    <Input
                      type="number"
                      min={1}
                      max={10}
                      value={formData.weight}
                      onChange={(e) => setFormData({ ...formData, weight: parseInt(e.target.value) || 1 })}
                      data-testid="question-weight-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('order')}</Label>
                    <Input
                      type="number"
                      min={0}
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                      data-testid="question-order-input"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" className="flex-1 rounded-full" onClick={() => setIsOpen(false)}>
                    {t('cancel')}
                  </Button>
                  <Button type="submit" className="flex-1 rounded-full" data-testid="save-question-btn">
                    <Save className="w-4 h-4 mr-2" />
                    {editingQuestion ? t('update') : t('create')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filter */}
        <div className="mb-6">
          <Select value={filterSymptom} onValueChange={setFilterSymptom}>
            <SelectTrigger className="w-64 rounded-full" data-testid="filter-symptom">
              <SelectValue placeholder={t('filterBySymptom')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('allSymptoms')}</SelectItem>
              {symptoms.map(symptom => (
                <SelectItem key={symptom.id} value={symptom.id}>{getSymptomName(symptom)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Questions list */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filteredQuestions.length > 0 ? (
          <div className="space-y-3">
            {filteredQuestions.map((question, index) => (
              <motion.div
                key={question.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: index * 0.05 }}
              >
                <Card className="p-4 rounded-2xl" data-testid={`question-item-${index}`}>
                  <div className="flex items-start gap-3">
                    <div className="p-2 text-muted-foreground cursor-grab">
                      <GripVertical className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{getQuestionText(question)}</p>
                      {/* Show translation status */}
                      <div className="flex gap-2 mt-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${question.text ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'}`}>
                          🇬🇧 {question.text ? '✓' : '✗'}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${question.text_pt ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'}`}>
                          🇵🇹 {question.text_pt ? '✓' : '✗'}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {question.symptom_ids.map(symId => {
                          const sym = symptoms.find(s => s.id === symId);
                          return sym ? (
                            <span key={symId} className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full">
                              {getSymptomName(sym)}
                            </span>
                          ) : null;
                        })}
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                        <span>{t('weight')}: {question.weight}</span>
                        <span>{t('order')}: {question.order}</span>
                        <span>{t('conditions')}: {question.condition_ids.length}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="rounded-full"
                        onClick={() => handleOpenDialog(question)}
                        data-testid={`edit-question-${index}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="rounded-full text-destructive"
                        onClick={() => handleDelete(question.id)}
                        data-testid={`delete-question-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        ) : (
          <Card className="p-8 rounded-2xl text-center">
            <HelpCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-semibold mb-2">{t('noQuestionsYet')}</h3>
            <p className="text-muted-foreground text-sm">{t('createFirstQuestion')}</p>
          </Card>
        )}
      </motion.div>
    </div>
  );
};
